import React, { useEffect, useRef } from 'react';
import type { StrongsEntry } from '../utils/strongsParser';

interface StrongsPopupProps {
  entry: StrongsEntry;
  position: { x: number; y: number };
  onClose: () => void;
}

export function StrongsPopup({ entry, position, onClose }: StrongsPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Position the popup near the clicked word
  useEffect(() => {
    if (!popupRef.current) return;

    const popup = popupRef.current;
    const rect = popup.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = position.x;
    let y = position.y + 10; // 10px below cursor

    // Adjust if popup would go off the right edge
    if (x + rect.width > viewportWidth - 20) {
      x = viewportWidth - rect.width - 20;
    }

    // Adjust if popup would go off the bottom edge
    if (y + rect.height > viewportHeight - 20) {
      y = position.y - rect.height - 10; // Show above cursor instead
    }

    // Ensure minimum distance from left edge
    if (x < 20) {
      x = 20;
    }

    // Ensure minimum distance from top edge
    if (y < 20) {
      y = 20;
    }

    popup.style.left = `${x}px`;
    popup.style.top = `${y}px`;
  }, [position]);

  // Parse HTML in definition (convert <br> tags to actual line breaks)
  const formatDefinition = (html: string) => {
    return { __html: html.replace(/<br>/gi, '<br/>') };
  };

  return (
    <>
      <div className="strongs-popup-overlay" onClick={onClose} />
      <div ref={popupRef} className="strongs-popup">
        <div className="strongs-popup-header">
          <div className="strongs-popup-title">
            <div className="strongs-popup-number">{entry.strongsNum}</div>
            <div className="strongs-popup-word">{entry.word}</div>
            {entry.transliteration && (
              <div className="strongs-popup-transliteration">
                {entry.transliteration}
              </div>
            )}
          </div>
          <button
            className="strongs-popup-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {entry.morphology && (
          <div className="strongs-popup-morphology">{entry.morphology}</div>
        )}

        {entry.gloss && (
          <div className="strongs-popup-gloss">{entry.gloss}</div>
        )}

        {entry.definition && (
          <div
            className="strongs-popup-definition"
            dangerouslySetInnerHTML={formatDefinition(entry.definition)}
          />
        )}
      </div>
    </>
  );
}
